import { useCallback } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import { ProjectPayload, ProjectState } from "./useProjects.types";
import { transformProjectArray } from "./projectTransform";
import { projectKeys } from "../../queries/project.keys";
import { fetchProjectDetail, getProjectDetailQueryKey } from "./projectDetailQuery";
import { useConditionalRefetchInterval } from "hooks/useConditionalRefetchInterval";
import { useRefetchOnPageVisible } from "hooks/useRefetchOnPageVisible";

// Type for the result of useProject hook
export type UseProjectResult = UseQueryResult<ProjectState | null, Error>;

/**
 * Factory function to create a hook for fetching a single project
 */
export const getUseProject = (axios: AxiosInstance) => {
  return (
    projectId?: string | number,
    enablePolling?: boolean,
    documentIdForFeatureStatuses?: string | number
  ): UseProjectResult => {
    const idStr = String(projectId ?? "");
    const isValidProjectId = idStr !== "" && idStr !== "undefined" && idStr !== "0";
    const enabled = isValidProjectId;
    const documentScope =
      documentIdForFeatureStatuses != null &&
      String(documentIdForFeatureStatuses) !== "" &&
      String(documentIdForFeatureStatuses) !== "0"
        ? String(documentIdForFeatureStatuses)
        : null;

    const baseInterval = enablePolling ? 5000 : false;
    const conditionalInterval = useConditionalRefetchInterval(baseInterval);

    const detailKey = isValidProjectId
      ? getProjectDetailQueryKey(idStr, documentScope ?? undefined)
      : projectKeys.detail(0);

    const queryResult = useQuery<ProjectPayload | null, Error, ProjectState | null>({
      queryKey: detailKey,
      enabled,
      queryFn: async () => {
        if (!enabled) throw new Error("Invalid project ID");

        return fetchProjectDetail(axios, idStr, {
          includeVersions: true,
          documentIdForFeatureStatuses: documentScope ?? undefined,
        });
      },
      staleTime: enablePolling ? 0 : 60 * 1000, // No stale time when polling to ensure fresh data
      refetchInterval: conditionalInterval,
      refetchIntervalInBackground: false,
      select: (data: ProjectPayload | null) => {
        // Always use fresh data from API calls
        const result = data ? transformProjectArray([data])[0] : null;
        return result;
      },
    }) as UseProjectResult;

    // While polling for an AI run, refresh as soon as the tab returns instead of waiting
    // for the next interval tick. `cancelRefetch: false` joins a fetch already in flight,
    // so several pollers on the same project share one request.
    const { refetch } = queryResult;
    const refetchOnVisible = useCallback(() => {
      void refetch({ cancelRefetch: false });
    }, [refetch]);
    useRefetchOnPageVisible(Boolean(enablePolling) && enabled, refetchOnVisible);

    return queryResult;
  };
};
